import React from 'react';
import PropTypes from 'prop-types';
import { Button, LinearProgress } from '@material-ui/core';
import {
  Sync as SyncIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  FiberManualRecord as RunningIcon,
} from '@material-ui/icons';
import clsx from 'clsx';
import { IndexingStatus } from '../../operatorState';
import { formatLastSyncTimeLocalized } from '../../../catalog-sync/catalogSyncUtils';
import { catalogSourcePhaseLabel, catalogSourceProgress } from '../../../catalog-sync/catalogSourcePhase';
import styles from '../OperatorHome/OperatorHome.module.scss';
import cardStyles from './CatalogSyncControlCard.module.scss';
// Helpers
// ---------------------------------------------------------------------------
function statusIcon(status) {
  if (status === IndexingStatus.RUNNING) return <RunningIcon fontSize="inherit" />;
  if (status === IndexingStatus.ERROR) return <ErrorIcon fontSize="inherit" />;
  if (status === IndexingStatus.READY) return <CheckCircleIcon fontSize="inherit" />;
  return <SyncIcon fontSize="inherit" />;
}

function iconClass(status) {
  if (status === IndexingStatus.RUNNING) return styles.iconNeutral;
  if (status === IndexingStatus.ERROR) return styles.iconErr;
  if (status === IndexingStatus.READY) return styles.iconOk;
  return styles.iconNeutral;
}

function cardClass(status) {
  if (status === IndexingStatus.ERROR) return styles.cardError;
  if (status === IndexingStatus.READY) return styles.cardSuccess;
  return null;
}

function progressText(progress, indexedCount, totalCount, isStarting, isFinalizing, localizeMessage) {
  if (isStarting) return localizeMessage('catalog.sync.starting');
  if (isFinalizing) return localizeMessage('catalog.sync.finalizing');
  if (totalCount > 0) {
    return localizeMessage('catalog.sync.indexedProgress', {
      indexed: indexedCount.toLocaleString(),
      total: totalCount.toLocaleString(),
    });
  }
  return localizeMessage('catalog.sync.percentComplete', { progress });
}
// CatalogSyncControlCard
// ---------------------------------------------------------------------------
/**
 * Standalone "Catalog Sync" control block for the Home operator screen.


 * Shows:
 *  - Last full update timestamp (or "Never")
 *  - Always-visible info note about incremental updates
 *  - Run Full Update button
 *  - Live progress bar + metrics while running/queued
 *  - Error state with retry action
 */
function CatalogSyncControlCard({
  status,
  progress,
  indexedCount,
  totalCount,
  lastSuccessAt,
  isStarting,
  isLoading,
  error,
  rawStatus,
  queueAgeSeconds,
  phase,
  sourceProcessed,
  sourceTotal,
  onStartFullUpdate,
  localizeMessage,
}) {
  const isQueued = rawStatus === 'queued';
  const source = { phase, sourceProcessed, sourceTotal };
  const sourceLabel = catalogSourcePhaseLabel(source, localizeMessage);
  const sourceProgress = catalogSourceProgress(source);
  const isFinalizing = rawStatus === 'running' && totalCount > 0 && indexedCount >= totalCount;
  const indeterminate = sourceLabel
    ? sourceProgress === null
    : (isStarting && !progress) || isQueued || isFinalizing;
  const isActive = status === IndexingStatus.RUNNING || isStarting || isQueued;
  const isError = status === IndexingStatus.ERROR;
  const canStart = !isLoading && !isActive;
  let actionLabel = localizeMessage('catalog.sync.run_btn');
  if (isActive) {
    actionLabel = localizeMessage('catalog.sync.running');
  } else if (isError) {
    actionLabel = localizeMessage('catalog.sync.retry_btn');
  }

  return (
    <div
      className={clsx(styles.card, cardClass(status), cardStyles.card)}
      data-testid="catalog-sync-card"
    >
      {/* Icon */}
      <div className={clsx(styles.cardIcon, iconClass(status))}>
        {statusIcon(status)}
      </div>

      {/* Body */}
      <div className={clsx(styles.cardBody, cardStyles.body)}>
        <p className={styles.cardTitle}>{localizeMessage('catalog.sync.title')}</p>

        {/* Last full update line */}
        <p className={styles.cardValue} data-testid="catalog-sync-last-update">
          {localizeMessage('catalog.sync.last_update')}
          {' '}
          {formatLastSyncTimeLocalized(lastSuccessAt, localizeMessage)}
        </p>

        {/* Incremental updates always-visible info note */}
        <p className={clsx(styles.cardHint, cardStyles.infoNote)} data-testid="catalog-sync-incremental-note">
          {localizeMessage('catalog.sync.incremental_note')}
        </p>

        {/* Progress bar + metrics (running only) */}
        {isActive && (
          <div className={cardStyles.progressBlock} data-testid="catalog-sync-progress-block">
            <p className={cardStyles.progressText} data-testid="catalog-sync-progress-text">
              {sourceLabel || (isQueued
                ? localizeMessage('catalog.sync.queued_waiting')
                : progressText(progress, indexedCount, totalCount, isStarting && !progress, isFinalizing, localizeMessage))}
            </p>
            <LinearProgress
              variant={indeterminate ? 'indeterminate' : 'determinate'}
              value={sourceLabel ? sourceProgress ?? undefined : progress}
              data-testid="catalog-sync-progress-bar"
            />
          </div>
        )}

        {/* Error message */}
        {isError && error && (
          <p className={clsx(styles.cardHint, cardStyles.errorText)} data-testid="catalog-sync-error">
            {error}
          </p>
        )}
      </div>

      {/* Action */}
      <div className={styles.cardAction}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={!canStart}
          onClick={onStartFullUpdate}
          data-testid="catalog-sync-run-btn"
        >
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}

CatalogSyncControlCard.propTypes = {
  status: PropTypes.string.isRequired,
  progress: PropTypes.number,
  indexedCount: PropTypes.number,
  totalCount: PropTypes.number,
  lastSuccessAt: PropTypes.string,
  isStarting: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  rawStatus: PropTypes.string,
  queueAgeSeconds: PropTypes.number,
  phase: PropTypes.string,
  sourceProcessed: PropTypes.number,
  sourceTotal: PropTypes.number,
  onStartFullUpdate: PropTypes.func.isRequired,
  localizeMessage: PropTypes.func.isRequired,
};

CatalogSyncControlCard.defaultProps = {
  progress: 0,
  indexedCount: 0,
  totalCount: 0,
  lastSuccessAt: null,
  isStarting: false,
  isLoading: false,
  error: null,
  rawStatus: 'idle',
  queueAgeSeconds: null,
  phase: null,
  sourceProcessed: 0,
  sourceTotal: 0,
};

export default CatalogSyncControlCard;
